<!-- MICROPHONE -->
<ion-item *ngIf="!backstage">
  <ion-fab-button
    *ngIf="!mute && ui === 'green-room'"
    aria-label="Toggle Microphone Off"
    tooltip="Toggle Microphone Off"
    (click)="toggleMute()"
    color="medium"
    slot="start"
    size="small">
    <ion-icon src="/assets/icons/24px/microphone.svg"> </ion-icon>
  </ion-fab-button>

  <ion-fab-button
    *ngIf="mute && ui === 'green-room'"
    aria-label="Toggle Microphone On"
    tooltip="Toggle Microphone On"
    (click)="toggleMute()"
    color="medium"
    slot="start"
    size="small">
    <ion-icon src="/assets/icons/24px/microphone-off.svg"> </ion-icon>
  </ion-fab-button>

  <ion-icon
    *ngIf="ui !== 'green-room'"
    [src]="mute ? '/assets/icons/24px/microphone-off.svg' : '/assets/icons/24px/microphone.svg'"
    slot="start">
  </ion-icon>

  <ion-select
    *ngIf="microphones.size; else noMicrophonesLabel"
    class="microphone-select"
    [value]="microphone"
    [compareWith]="compareDevices"
    [interfaceOptions]="{ cssClass: 'equipment-menu' }"
    interface="popover"
    (ionChange)="selectMicrophone($event)"
    [disabled]="recording"
    aria-label="Select Microphone">
    <ion-select-option *ngFor="let microphone of microphones | keyvalue" [value]="microphone.value">
      {{ filteredNames.get(microphone.value.label) }}
    </ion-select-option>
  </ion-select>

  <ng-template #noMicrophonesLabel>
    <ion-label (click)="openMicrophoneInfoMenu($event)" class="pointer ion-text-wrap">
      Microphone<br />
      <small>Required</small>
    </ion-label>
  </ng-template>

  <ion-button
    *ngIf="ui !== 'name-menu'"
    (click)="openMicrophoneInfoMenu($event)"
    aria-label="Learn More about Microphones"
    tooltip="Learn More about Microphones"
    class="help"
    fill="clear"
    rel="help">
    <ion-icon src="/assets/icons/16px/info-outline.svg" slot="icon-only"> </ion-icon>
  </ion-button>
</ion-item>

<!-- CAMERA -->
<div *ngIf="!backstage" class="camera-options">
  <ion-item
    class="camera-select-item"
    [class.in-name-menu]="ui === 'name-menu'"
    [class.video-disabled]="!(studioSession$ | async)?.videoEnabled">
    <ion-fab-button
      *ngIf="cameras.size && !videoOff && ui === 'green-room'"
      aria-label="Toggle Video Off"
      tooltip="Toggle Video Off"
      (click)="toggleVideoOff()"
      color="medium"
      slot="start"
      size="small">
      <ion-icon src="/assets/icons/24px/camera.svg"> </ion-icon>
    </ion-fab-button>

    <ion-fab-button
      *ngIf="cameras.size && videoOff && ui === 'green-room'"
      aria-label="Toggle Video On"
      tooltip="Toggle Video On"
      (click)="toggleVideoOff()"
      color="medium"
      slot="start"
      size="small">
      <ion-icon src="/assets/icons/24px/camera-off.svg"> </ion-icon>
    </ion-fab-button>

    <ion-icon *ngIf="!cameras.size" src="/assets/icons/24px/camera-off.svg" slot="start"> </ion-icon>

    <ion-icon
      *ngIf="cameras.size && ui !== 'green-room'"
      [src]="videoOff ? '/assets/icons/24px/camera-off.svg' : '/assets/icons/24px/camera.svg'"
      slot="start">
    </ion-icon>

    <ion-select
      *ngIf="cameras.size; else noCamerasLabel"
      [value]="camera"
      [compareWith]="compareDevices"
      [interfaceOptions]="{ cssClass: 'equipment-menu' }"
      interface="popover"
      (ionChange)="selectCamera($event)"
      [disabled]="recording"
      aria-label="Select Camera">
      <ion-select-option *ngFor="let camera of cameras | keyvalue" [value]="camera.value">
        {{ filteredNames.get(camera.value.label) }}
      </ion-select-option>
    </ion-select>

    <ng-template #noCamerasLabel>
      <ion-label (click)="openCameraInfoMenu($event)" class="pointer ion-text-wrap">
        Camera<br />
        <small>Optional</small>
      </ion-label>
    </ng-template>
  </ion-item>

  <ion-item
    *ngIf="(studioSession$ | async).videoEnabled"
    class="resolution-select-item"
    [class.in-name-menu]="ui === 'name-menu'"
    [tooltip]="(selectedResolution$ | async) === 'UHD' ? '4K Recordings are currently in beta!' : ''">
    <ion-select
      #resolutionSelect
      interface="popover"
      [disabled]="ui === 'name-menu' && recording"
      [value]="selectedResolution$ | async"
      (ionChange)="selectResolution($event)">
      <ion-select-option
        *ngFor="let resolution of resolutionNames"
        [value]="resolution"
        [disabled]="!checkSupported(resolution)">
        {{ videoResolutionsForDisplay[resolution] }}
      </ion-select-option>
    </ion-select>
  </ion-item>

  <ion-button
    *ngIf="ui !== 'name-menu'"
    (click)="openCameraInfoMenu($event)"
    aria-label="Learn More about Cameras"
    tooltip="Learn More about Cameras"
    class="help"
    fill="clear"
    rel="help">
    <ion-icon src="/assets/icons/16px/info-outline.svg" slot="icon-only"> </ion-icon>
  </ion-button>
</div>

<!-- HEADPHONE -->
<div>
  <ion-item>
    <ion-icon src="/assets/icons/24px/headphones.svg" slot="start"> </ion-icon>

    <ion-select
      *ngIf="headphones.size && !isAndroid && !isFirefox; else noHeadphonesLabel"
      [value]="headphone"
      [compareWith]="compareDevices"
      [interfaceOptions]="{ cssClass: 'equipment-menu' }"
      interface="popover"
      (ionChange)="selectHeadphone($event)"
      [disabled]="recording"
      aria-label="Select Headphones">
      <ion-select-option *ngFor="let headphone of headphones | keyvalue" [value]="headphone.value">
        {{ filteredNames.get(headphone.value.label) }}
      </ion-select-option>
    </ion-select>

    <ng-template #noHeadphonesLabel>
      <ion-label (click)="openHeadphonesInfoMenu($event)" class="pointer ion-text-wrap">
        Using System Default<br />
        <p *ngIf="backstage && !isFirefox && !isAppleDevice && !isAndroid">
          Permissions needed to change.<sc-learn-more topic="selectEquipment"></sc-learn-more>
        </p>
        <p *ngIf="isFirefox">
          Firefox uses your default system output.
          <sc-learn-more *ngIf="ui !== 'green-room'" topic="selectEquipment"></sc-learn-more>
        </p>
        <p *ngIf="isAppleDevice">
          Safari uses your default system output.
          <sc-learn-more *ngIf="ui !== 'green-room'" topic="selectEquipment"></sc-learn-more>
        </p>
        <p *ngIf="isAndroid">
          Android uses your default system output.
          <sc-learn-more *ngIf="ui !== 'green-room'" topic="selectEquipment"></sc-learn-more>
        </p>
      </ion-label>
      <ion-button
        *ngIf="backstage && !isFirefox && !isAppleDevice"
        class="grant-button"
        slot="end"
        (click)="getUserMedia()">
        Grant
      </ion-button>
    </ng-template>

    <ion-button
      *ngIf="ui !== 'name-menu'"
      (click)="openHeadphonesInfoMenu($event)"
      aria-label="Learn More about Headphones"
      tooltip="Learn More about Headphones"
      class="help"
      fill="clear"
      rel="help">
      <ion-icon src="/assets/icons/16px/info-outline.svg" slot="icon-only"> </ion-icon>
    </ion-button>
  </ion-item>
</div>
